import { useState, useEffect, useMemo } from 'react';
import { FileUpload } from '@/components/application/file-upload/file-upload-base';
import { UploadStatusCard } from '@/components/application/upload-status/upload-status-card';
import { Accordion } from '@/components/application/accordion/accordion';
import { Badge } from '@/components/base/badges/badges';
import { useFilesPolling } from '@/hooks/use-files-polling';
import { 
  getGreeting, 
  mapInvoiceStatusToUploadStatus,
  deriveInvoiceProcessingStatus,
  transformWarningToDetailedIssues,
  generateAnalysisSummary,
  deriveVarianceInfo
} from '@/lib/invoice-helpers';
import { stateGroups } from '@/config/upload-states';
import type { UploadedFile } from '@/types/upload-file';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function HomePage() {
  const [greeting, setGreeting] = useState<string>('Hello');
  
  // Use custom hook for file management and polling
  const { files, uploadFiles, deleteFile, fetchExistingFiles } = useFilesPolling();

  // Set greeting only on client side
  useEffect(() => {
    setGreeting(getGreeting());
  }, []);

  // Fetch existing files on mount
  useEffect(() => {
    fetchExistingFiles();
  }, [fetchExistingFiles]);

  const handleAction = (action: string, fileId: string) => {
    if (action === 'Remove' || action === 'Cancel') {
      deleteFile(fileId);
    } else {
      console.log(`${action} triggered for file ${fileId}`);
    }
  };

  // Flatten files into display items (Files or Invoices)
  const flattenedItems = useMemo(() => {
    const items: (UploadedFile & { originalFileId?: string })[] = [];
    
    files.forEach(file => {
      // If file has invoices, create an item for each invoice
      if (file.invoices && file.invoices.length > 0) {
        file.invoices.forEach((inv, index) => {
           // Calculate issues for this specific invoice
           const detailedIssues = inv.warnings?.flatMap(w => transformWarningToDetailedIssues(w)) || [];
           const issues = detailedIssues.map(i => `${i.description} ${i.impact ? `(${i.impact})` : ''}`);
           
           const hasIssues = detailedIssues.length > 0;
           const uiStatus = mapInvoiceStatusToUploadStatus(inv.status, hasIssues);
           
           // Derive processing status for invoice
           const processingStatus = deriveInvoiceProcessingStatus(inv.status);
           
           // Analysis summary
           const analysisSummary = generateAnalysisSummary(detailedIssues, inv.vendor);
           
           // Variance info
           const varianceInfo = deriveVarianceInfo(inv.balance);

           items.push({
               ...file, // Inherit file props
               id: inv.recordId || `${file.id}-${index}`, // Unique ID from invoice
               originalFileId: file.id, // Keep reference to original file ID for actions
               name: file.name, // Keep file name for "filename" prop, but card will show vendor in header
               invoices: [inv], // Pass ONLY this invoice
               status: uiStatus,
               processingStatus: processingStatus, // Override processing status
               issues: issues,
               detailedIssues: detailedIssues.length > 0 ? detailedIssues : undefined,
               analysisSummary: analysisSummary,
               varianceInfo: varianceInfo,
               // Use invoice error info if available, otherwise undefined (don't show file error on successful invoice)
               errorCode: inv.errorCode || undefined, 
               errorDescription: inv.errorDescription || undefined,
           });
        });
      } else {
        // No invoices yet, or file-level error -> Show file card
        items.push(file);
      }
    });
    return items;
  }, [files]);

  // Group and sort files by state
  const groupedFiles = useMemo(() => {
    const groups: Map<string, (UploadedFile & { originalFileId?: string })[]> = new Map();

    stateGroups.forEach(group => {
      groups.set(group.id, []);
    });

    flattenedItems.forEach(file => {
      const group = stateGroups.find(g => g.statuses.includes(file.status));
      if (group) {
        const groupFiles = groups.get(group.id) || [];
        groupFiles.push(file);
        groups.set(group.id, groupFiles);
      } else {
        // Fallback for unknown statuses
        const activeGroup = groups.get('active');
        activeGroup?.push(file);
      }
    });

    // Sort files within each group by createdAt (newest first)
    groups.forEach((groupFiles, groupId) => {
      groupFiles.sort((a, b) => {
        const aTime = a.createdAt?.getTime() ?? 0;
        const bTime = b.createdAt?.getTime() ?? 0;
        return bTime - aTime;
      });
      groups.set(groupId, groupFiles);
    });

    return groups;
  }, [flattenedItems]);

  // Get groups with files, sorted by priority
  const activeGroups = useMemo(() => {
    return stateGroups
      .filter(group => (groupedFiles.get(group.id) || []).length > 0)
      .sort((a, b) => b.priority - a.priority);
  }, [groupedFiles]);

  return (
    <div className="h-full overflow-auto bg-primary">
      <div className="container mx-auto max-w-4xl p-6">
        {/* Greeting Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-secondary mb-2">{greeting}, Sherry</h1>
          <p className="text-tertiary">
            Upload your documents to get started with automated invoice processing.
          </p>
        </div>

        {/* Upload Area */}
        <FileUpload.Root>
          <FileUpload.DropZone
            hint="PDF and image files accepted up to 50MB"
            maxSize={50 * 1024 * 1024}
            allowsMultiple={true}
            accept="image/*,application/pdf"
            onDropFiles={uploadFiles}
            onDropUnacceptedFiles={(rejectedFiles) => {
              console.log('Rejected files:', rejectedFiles);
            }}
            onSizeLimitExceed={(oversizedFiles) => {
              console.log('Oversized files:', oversizedFiles);
            }}
          />
        </FileUpload.Root>

        {/* Files by State Group */}
        {activeGroups.length > 0 && (
          <div className="mt-8">
            <Accordion.Root 
              allowMultiple 
              persistKey="upload-status-groups"
              className="space-y-2"
              defaultExpanded={['active', 'needs-review', 'errors']}
            >
              {activeGroups.map((group) => {
                const groupFiles = groupedFiles.get(group.id) || [];
                
                return (
                  <Accordion.Item key={group.id} id={group.id} itemCount={groupFiles.length}>
                    <Accordion.Trigger
                      badge={
                        <Badge size="sm" color={group.badgeColor} type="color">
                          {groupFiles.length}
                        </Badge>
                      }
                    >
                      {group.label}
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <div className="space-y-4 pt-2">
                        {groupFiles.map((file) => (
                          <UploadStatusCard
                            key={file.id}
                            filename={file.name}
                            status={file.status}
                            processingStatus={file.processingStatus as any}
                            pageCount={file.pageCount}
                            fileSize={file.size}
                            invoices={file.invoices}
                            issues={file.issues}
                            detailedIssues={file.detailedIssues}
                            analysisSummary={file.analysisSummary}
                            varianceInfo={file.varianceInfo}
                            errorCode={file.errorCode}
                            errorMessage={file.errorDescription}
                            duplicateInfo={file.duplicateInfo ? {
                                originalFilename: 'Original File', // We might want to pass this if available
                                uploadedDate: 'Previously'
                            } : undefined}
                            onCancel={() => handleAction('Cancel', file.originalFileId || file.id)}
                            onExport={() => handleAction('Export', file.originalFileId || file.id)}
                            onRemove={() => handleAction('Remove', file.originalFileId || file.id)}
                            onGetHelp={() => handleAction('Get Help', file.originalFileId || file.id)}
                            onViewFile={() => handleAction('View File', file.originalFileId || file.id)}
                            onReprocess={() => handleAction('Reprocess', file.originalFileId || file.id)}
                            onContactVendor={() => handleAction('Contact Vendor', file.originalFileId || file.id)}
                          />
                        ))}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                );
              })}
            </Accordion.Root>
          </div>
        )}
      </div>
    </div>
  );
}
